import React from 'react';
import { Info, Mail, Code, Smartphone, FileCode, Globe, AlertTriangle, CheckSquare } from 'lucide-react';

/**
 * Компонент с информацией о mailto-ссылках и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о Mailto-ссылках: руководство и советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-indigo-500" />
          Что такое Mailto-ссылка?
        </h3>
        <p>
          <strong>Mailto-ссылка</strong> — это специальный тип URL, который при нажатии открывает почтовый клиент пользователя (Gmail, Outlook, Mail.ru, Яндекс.Почта и др.) с уже заполненными полями: получатель, тема, текст письма. Синтаксис Mailto-ссылки начинается с протокола <code>mailto:</code>, за которым следует адрес электронной почты и опциональные параметры.
        </p>

        <h3 className="flex items-center gap-2">
          <Code className="w-5 h-5 text-indigo-500" />
          Структура Mailto-ссылки
        </h3>
        <p>
          Базовый синтаксис mailto-ссылки выглядит так:
        </p>
        <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto">
          <code>mailto:email@example.ru?subject=Тема&body=Текст%20письма</code>
        </pre>
        <p>Mailto-ссылка может содержать следующие параметры:</p>
        <ul>
          <li><strong>to</strong> — адрес получателя (может быть несколько через запятую)</li>
          <li><strong>cc</strong> — адреса для копии письма</li>
          <li><strong>bcc</strong> — адреса для скрытой копии</li>
          <li><strong>subject</strong> — тема письма</li>
          <li><strong>body</strong> — содержимое письма</li>
        </ul>
        <p>
          Параметры добавляются через знак вопроса <code>?</code>, а разделяются между собой знаком амперсанда <code>&</code>. Пробелы и специальные символы должны быть закодированы для URL.
        </p>

        <h3 className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-indigo-500" />
          Как работают Mailto-ссылки на разных устройствах?
        </h3>
        <p>
          При клике на mailto-ссылку поведение зависит от устройства и настроек пользователя:
        </p>
        <ul>
          <li><strong>Компьютеры</strong>: Открывается почтовый клиент, установленный по умолчанию (Outlook, Thunderbird, Mail и т.д.)</li>
          <li><strong>Мобильные устройства</strong>: Открывается почтовое приложение, установленное по умолчанию</li>
          <li><strong>Веб-браузеры</strong>: Может открыться веб-интерфейс почтового сервиса (Gmail, Mail.ru), если он настроен по умолчанию</li>
        </ul>
        <p>
          Важно помнить, что не у всех пользователей настроен почтовый клиент, поэтому рекомендуется всегда дублировать контактную информацию в текстовом виде.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Совет для российских пользователей</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            При создании mailto-ссылок для российской аудитории обязательно включайте опцию "Корректное отображение русских букв", чтобы избежать проблем с кодировкой кириллицы в почтовых клиентах.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <FileCode className="w-5 h-5 text-indigo-500" />
          Примеры использования Mailto-ссылок
        </h3>
        <ol>
          <li>
            <strong>Базовая ссылка для отправки письма:</strong>
            <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg mt-1 mb-2">
              <code>&lt;a href="mailto:info@example.ru"&gt;Написать нам&lt;/a&gt;</code>
            </pre>
          </li>
          <li>
            <strong>Ссылка с темой и содержанием:</strong>
            <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg mt-1 mb-2">
              <code>&lt;a href="mailto:info@example.ru?subject=Вопрос по заказу&body=Здравствуйте, у меня вопрос..."&gt;Задать вопрос&lt;/a&gt;</code>
            </pre>
          </li>
          <li>
            <strong>Ссылка с несколькими получателями:</strong>
            <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg mt-1 mb-2">
              <code>&lt;a href="mailto:info@example.ru,support@example.ru"&gt;Написать в поддержку&lt;/a&gt;</code>
            </pre>
          </li>
          <li>
            <strong>Ссылка с копией (CC) и скрытой копией (BCC):</strong>
            <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded-lg mt-1 mb-2">
              <code>&lt;a href="mailto:manager@example.ru?cc=info@example.ru&bcc=director@example.ru"&gt;Связаться с менеджером&lt;/a&gt;</code>
            </pre>
          </li>
        </ol>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-indigo-500" />
          Применение Mailto-ссылок на сайтах
        </h3>
        <p>
          Mailto-ссылки широко используются в следующих случаях:
        </p>
        <ul>
          <li><strong>Контактная информация</strong> — на страницах "Контакты", "О нас", в подвале сайта</li>
          <li><strong>Формы обратной связи</strong> — как альтернатива для пользователей, предпочитающих использовать свой почтовый клиент</li>
          <li><strong>Email-маркетинг</strong> — для создания предзаполненных писем с определенным содержанием</li>
          <li><strong>Кнопки "Поделиться по email"</strong> — для отправки ссылок на статьи, товары</li>
          <li><strong>Подписи к email</strong> — для быстрого ответа на определенные типы запросов</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Ограничения Mailto-ссылок
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Не позволяют прикреплять файлы (вложения)</li>
            <li>Зависят от настроек почтового клиента пользователя</li>
            <li>Имеют ограничения на длину (особенно в поле body)</li>
            <li>Могут возникать проблемы с кодировкой при использовании кириллицы</li>
            <li>Не всегда корректно работают на мобильных устройствах с несколькими почтовыми приложениями</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-indigo-500" />
          Лучшие практики использования
        </h3>
        <ul>
          <li>Обязательно указывайте понятный текст ссылки, а не просто email-адрес</li>
          <li>Используйте иконку конверта или другие визуальные подсказки для обозначения mailto-ссылок</li>
          <li>Всегда дублируйте контактный email в текстовом виде для возможности копирования</li>
          <li>Не переусложняйте письма — пользователь всегда может изменить содержимое</li>
          <li>Для поддержки кириллицы используйте корректную кодировку (encodeURI вместо encodeURIComponent)</li>
          <li>Тестируйте ссылки в разных почтовых клиентах и на разных устройствах</li>
          <li>Для SEO-оптимизации защищайте email-адреса от спам-ботов с помощью JavaScript или других методов</li>
        </ul>

        <h3>Настройка UTM-меток для отслеживания</h3>
        <p>
          Для email-маркетинга и аналитики полезно добавлять UTM-метки в тело письма или содержащиеся в нем ссылки:
        </p>
        <ul>
          <li><strong>utm_source</strong> — источник перехода (обычно "email")</li>
          <li><strong>utm_medium</strong> — тип рекламного канала (например, "mailto" или "signature")</li>
          <li><strong>utm_campaign</strong> — название конкретной кампании</li>
          <li><strong>utm_content</strong> — для различения разных ссылок внутри одного письма</li>
        </ul>
        <p>
          Такой подход позволяет отслеживать эффективность mailto-ссылок в Google Analytics, Яндекс.Метрике и других системах аналитики.
        </p>

        <p className="font-semibold mt-6">
          Наш генератор Mailto-ссылок автоматизирует все эти процессы, позволяя создавать корректно работающие ссылки без необходимости разбираться в тонкостях кодирования и форматирования!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;